import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './BusStopSearch.css';
import busStopsService from '../services/busStopsService';
import { trackEvent } from '../utils/analytics';

const BusStopSearch = ({ onSearchResults, onClear, language = 'tr', externalClear = 0 }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  const lastSearchQuery = useRef('');
  const previousQueryLength = useRef(0);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle external clear request (triggered when externalClear value changes)
  const prevExternalClear = useRef(externalClear);
  useEffect(() => {
    if (externalClear !== prevExternalClear.current) {
      prevExternalClear.current = externalClear;
      setQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      lastSearchQuery.current = '';
      previousQueryLength.current = 0;
    }
  }, [externalClear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Проверяем, изменилось ли количество символов
    const currentLength = query.length;
    const hasLengthChanged = currentLength !== previousQueryLength.current;

    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      if (query.length === 0 && lastSearchQuery.current.length > 0) {
        trackEvent('search_cleared');
        onClear();
        lastSearchQuery.current = '';
      }
      previousQueryLength.current = currentLength;
      return;
    }

    // Запрос идет только если изменилось количество символов
    if (!hasLengthChanged) {
      return;
    }

    previousQueryLength.current = currentLength;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        setLoading(true);
        lastSearchQuery.current = query;
        const results = await busStopsService.search(query, language);
        trackEvent('bus_stop_search', {
          query_length: query.length,
          results_count: results.length,
          language
        });
        setSuggestions(results);
        setShowSuggestions(true);
        onSearchResults(results.slice(0, 3), query);
      } catch (error) {
        trackEvent('bus_stop_search_failed', {
          query_length: query.length,
          language
        });
        console.error(t('search.error'), error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, language, onSearchResults, onClear, t]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    trackEvent('search_cleared');
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (lastSearchQuery.current.length > 0) {
      onClear();
      lastSearchQuery.current = '';
    }
  };

  const handleSuggestionClick = (suggestion) => {
    trackEvent('bus_stop_selected', {
      stop_number: suggestion.stopNumber
    });
    // Always use Turkish address
    const selectedText = suggestion.address;
    setQuery(selectedText);
    setShowSuggestions(false);
    lastSearchQuery.current = selectedText;
    previousQueryLength.current = selectedText.length;
    onSearchResults([suggestion], selectedText);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const getPlaceholder = () => {
    const isNumber = /^\d+$/.test(query);
    if (isNumber && query.length >= 4) {
      return t('search.placeholderNumber');
    }
    return t('search.placeholder');
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? <mark key={index}>{part}</mark> : part
    );
  };

  return (
    <div className="bus-stop-search" ref={searchRef}>
      <div className="search-input-wrapper">
        <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={getPlaceholder()}
        />
        {query && (
          <button className="clear-button" onClick={handleClear} type="button">
            ×
          </button>
        )}
        {loading && (
          <div className="search-loading">
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestions-list">
          {suggestions.slice(0, 3).map((suggestion, index) => (
            <li
              key={suggestion.id}
              className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-number">
                {highlightMatch(suggestion.stopNumber, query)}
              </div>
              <div className="suggestion-address">
                {highlightMatch(suggestion.address, query)}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showSuggestions && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="no-results">
          {t('search.noResults')}
        </div>
      )}
    </div>
  );
};

export default BusStopSearch;
